// src/context/NotificationContext.js

import React, { createContext, useContext } from 'react';
import { Toaster, toast } from 'sonner';

const NotificationContext = createContext({
    showNotification: () => {},
    hideNotification: () => {},
});

const DEFAULT_DURATION = 2000;
const DEFAULT_POSITION = 'top-right';

const normalizeSeverity = (severity) => {
    const normalized = String(severity || 'success').toLowerCase().trim();

    if (normalized === 'danger' || normalized === 'failed') {
        return 'error';
    }

    if (normalized === 'warn') {
        return 'warning';
    }

    if (normalized === 'ok') {
        return 'success';
    }

    if (['success', 'error', 'warning', 'info', 'loading'].includes(normalized)) {
        return normalized;
    }

    return 'success';
};

const normalizePosition = (position, anchorOrigin) => {
    if (typeof position === 'string') {
        return position;
    }

    const vertical = anchorOrigin?.vertical || 'top';
    const horizontal = anchorOrigin?.horizontal || 'right';
    const computed = `${vertical}-${horizontal}`;

    if ([
        'top-left',
        'top-center',
        'top-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
    ].includes(computed)) {
        return computed;
    }

    return DEFAULT_POSITION;
};

const normalizeAction = (action) => {
    if (!action) {
        return undefined;
    }

    if (typeof action === 'object' && action.label) {
        return {
            label: action.label,
            onClick: typeof action.onClick === 'function' ? action.onClick : () => {},
        };
    }

    if (React.isValidElement(action)) {
        const label = action.props?.children;
        const onClick = action.props?.onClick;

        if (typeof label === 'string' && typeof onClick === 'function') {
            return { label, onClick };
        }
    }

    return undefined;
};

const normalizeNotificationInput = (message, severity = 'success', options = {}) => {
    if (typeof message === 'object' && message !== null) {
        const config = message;
        return {
            message: config.message || '',
            title: config.title || '',
            description: config.description || '',
            severity: normalizeSeverity(config.severity),
            duration: config.autoHideDuration ?? DEFAULT_DURATION,
            position: normalizePosition(config.position, config.anchorOrigin),
            action: normalizeAction(config.action),
        };
    }

    return {
        message: message || '',
        title: options.title || '',
        description: options.description || '',
        severity: normalizeSeverity(severity),
        duration: options.autoHideDuration ?? DEFAULT_DURATION,
        position: normalizePosition(options.position, options.anchorOrigin),
        action: normalizeAction(options.action),
    };
};

const getToastMessage = (config) => {
    if (config.title) {
        return config.title;
    }

    const rawMessage = String(config.message || '').trim();
    const splitIndex = rawMessage.indexOf(': ');

    if (config.description || splitIndex <= 0 || splitIndex >= 36) {
        return rawMessage;
    }

    return rawMessage.slice(0, splitIndex);
};

const getToastDescription = (config) => {
    if (config.description) {
        return config.description;
    }

    const rawMessage = String(config.message || '').trim();
    const splitIndex = rawMessage.indexOf(': ');

    if (splitIndex > 0 && splitIndex < 36) {
        return rawMessage.slice(splitIndex + 2);
    }

    return '';
};

export const NotificationProvider = ({ children }) => {
    const showNotification = React.useCallback((message, severity = 'success', options = {}) => {
        const config = normalizeNotificationInput(message, severity, options);
        const toastMessage = getToastMessage(config);
        const toastDescription = getToastDescription(config);

        const toastOptions = {
            description: toastDescription || undefined,
            duration: config.duration,
            position: config.position,
            action: config.action,
            closeButton: true,
        };

        if (config.severity === 'success') {
            return toast.success(toastMessage, toastOptions);
        }

        if (config.severity === 'error') {
            return toast.error(toastMessage, toastOptions);
        }

        if (config.severity === 'warning') {
            return toast.warning(toastMessage, toastOptions);
        }

        if (config.severity === 'info') {
            return toast.info(toastMessage, toastOptions);
        }

        if (config.severity === 'loading') {
            return toast.loading(toastMessage, toastOptions);
        }

        return toast(toastMessage, toastOptions);
    }, []);

    const hideNotification = React.useCallback((toastId) => {
        if (typeof toastId === 'undefined') {
            toast.dismiss();
            return;
        }

        toast.dismiss(toastId);
    }, []);

    return (
        <NotificationContext.Provider value={{ showNotification, hideNotification }}>
            {children}
            <Toaster
                position={DEFAULT_POSITION}
                theme="dark"
                expand={false}
                richColors
                closeButton
                visibleToasts={5}
                offset={16}
                toastOptions={{
                    duration: DEFAULT_DURATION,
                    className: 'manager-sonner-toast',
                    style: {
                        borderRadius: '14px',
                        fontSize: '14px',
                        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(2, 6, 23, 0.98) 100%)',
                        color: '#e2e8f0',
                        border: '1px solid rgba(148, 163, 184, 0.22)',
                        boxShadow: '0 14px 40px rgba(2, 6, 23, 0.45)',
                    },
                }}
            />
        </NotificationContext.Provider>
    );
};

export const useNotification = () => useContext(NotificationContext);
